import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Bot, Loader2, Sparkles, Lock, Send, Copy, Check,
  Download, Trash2, FileCode2, FileText, History, ArrowRight, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAgentConfig, runAgent, listAgentRuns, deleteAgentRun, downloadAgentZip,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const TEMPLATES = [
  { label: "Landing page", prompt: "Generate a complete one-page landing site for a fitness coaching brand called 'IronFlow'. Hero, 3 feature cards, testimonial, CTA. Use Tailwind via CDN. Include OG tags and JSON-LD." },
  { label: "SEO bundle", prompt: "Generate a sitemap.xml, robots.txt, and a meta-tags.html with title + description + OG + Twitter cards for a SaaS product called 'Sabzy AI' at https://sabzy.in. Keep titles ≤60 chars, descriptions ≤155 chars." },
  { label: "Python automation", prompt: "Write a Python 3 script that scrapes the top 30 Hacker News stories and saves them as JSON each hour. Include a README.md and requirements.txt." },
  { label: "Email template", prompt: "Generate a transactional welcome email in HTML (table-based, dark-mode friendly) for a new user of Sabzy AI. Include plain-text fallback." },
];

export default function Agent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [activeFile, setActiveFile] = useState(0);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [useWebResearch, setUseWebResearch] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  // Auto-show preview when a run with index.html lands
  const currentFiles = current?.files;
  useEffect(() => {
    if (currentFiles?.some((f) => /index\.html?$/i.test(f.path))) {
      setShowPreview(true);
    }
  }, [currentFiles]);

  // Build the inlined srcdoc for the iframe — replaces external <link>/<script>
  // references with the inline file contents from this same run.
  const previewSrcDoc = useMemo(() => {
    if (!currentFiles) return "";
    const indexFile = currentFiles.find((f) => /index\.html?$/i.test(f.path));
    if (!indexFile) return "";
    let html = indexFile.content;
    const cssFiles = currentFiles.filter((f) => /\.css$/i.test(f.path));
    const jsFiles = currentFiles.filter((f) => /\.js$/i.test(f.path));

    cssFiles.forEach((f) => {
      const linkRe = new RegExp(`<link[^>]*href=["']\\.?\\/?${f.path.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}["'][^>]*>`, "gi");
      html = html.replace(linkRe, `<style>\n${f.content}\n</style>`);
    });
    jsFiles.forEach((f) => {
      const scriptRe = new RegExp(`<script[^>]*src=["']\\.?\\/?${f.path.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}["'][^>]*></script>`, "gi");
      html = html.replace(scriptRe, `<script>\n${f.content}\n</script>`);
    });
    return html;
  }, [currentFiles]);

  const hasPreview = Boolean(previewSrcDoc);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, navigate]);

  const refresh = async () => {
    try {
      const [c, r] = await Promise.all([getAgentConfig(), listAgentRuns()]);
      setConfig(c.data);
      setHistory(r.data?.runs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Sabzy Agent");
    }
  };

  const handleRun = async (e) => {
    e?.preventDefault();
    const p = prompt.trim();
    if (!p) return toast.error("Describe what you want me to build");
    setRunning(true);
    setCurrent(null);
    try {
      const res = await runAgent(p, useWebResearch);
      setCurrent(res.data);
      setActiveFile(0);
      setPrompt("");
      await refresh();
      toast.success(`Generated ${res.data.files?.length || 0} file${res.data.files?.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Agent run failed");
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this run?")) return;
    try {
      await deleteAgentRun(id);
      toast.success("Deleted");
      if (current?.id === id) setCurrent(null);
      await refresh();
    } catch { toast.error("Could not delete"); }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const isLocked = config && !config.available;
  const activeFileObj = useMemo(
    () => current?.files?.[activeFile] || null,
    [current, activeFile],
  );

  return (
    <div className="min-h-screen bg-background grain">
      <Seo title="Sabzy Agent — AI builder" description="Tell Sabzy Agent what to build — landing pages, SEO bundles, scripts, automations — and get downloadable files." path="/agent" noindex />

      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/chat")} data-testid="agent-back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Logo />
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 lg:px-8 lg:py-14">
        <div className="mb-7">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
            <Bot className="h-3 w-3" /> Sabzy Agent · multi-model
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Tell me what to build.</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Describe any deliverable — websites, SEO bundles, scripts, automations, audits — and I&apos;ll return a complete, downloadable project. Pro &amp; Pro Max only.
          </p>
        </div>

        {isLocked && (
          <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="agent-paywall">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-amber-500/15 text-amber-600">
                <Lock className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">Sabzy Agent is a Pro+ feature</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Pro: 20 runs/day · Pro Max: 100 runs/day. Each run generates a full multi-file deliverable.
                </div>
                <Button onClick={() => navigate("/upgrade")} className="mt-3 rounded-full" size="sm" data-testid="agent-upgrade-cta">
                  See plans
                </Button>
              </div>
            </div>
          </div>
        )}

        {!isLocked && config && (
          <div className="grid gap-6 lg:grid-cols-12">
            {/* Prompt + history */}
            <section className="space-y-4 lg:col-span-5" data-testid="agent-input-col">
              <form onSubmit={handleRun} className="rounded-2xl border border-border bg-card/50 p-4">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder='e.g. "Generate a landing page for my cafe with menu section, hours, address, and a Tailwind-powered hero."'
                  rows={6}
                  maxLength={8000}
                  className="resize-none rounded-xl text-sm"
                  data-testid="agent-prompt-input"
                />
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-[11px] text-muted-foreground">
                      {config.remaining_today} / {config.daily_limit} left today
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseWebResearch((v) => !v)}
                      className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition ${
                        useWebResearch
                          ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-500"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                      title="Search the web before generating — slower but more accurate"
                      data-testid="agent-web-research-toggle"
                    >
                      🌐 Web {useWebResearch ? "ON" : "OFF"}
                    </button>
                  </div>
                  <Button type="submit" disabled={running || !prompt.trim()} className="rounded-lg" data-testid="agent-run-btn">
                    {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Building…</> : <><Send className="mr-1.5 h-3.5 w-3.5" /> Build it</>}
                  </Button>
                </div>
              </form>

              <div className="flex flex-wrap gap-2" data-testid="agent-templates">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => { setPrompt(t.prompt); }}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-[11px] transition hover:border-primary/50 hover:text-primary"
                    data-testid={`agent-template-${t.label.toLowerCase().replace(/\s/g,'-')}`}
                  >
                    {t.label} <ArrowRight className="ml-1 inline h-3 w-3" />
                  </button>
                ))}
              </div>

              <div className="rounded-2xl border border-border/60 bg-card/30 p-4">
                <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  <History className="h-3 w-3" /> Recent runs
                </div>
                {history.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground">No runs yet.</div>
                ) : (
                  <ScrollArea className="h-[360px] pr-1">
                    <div className="space-y-1.5">
                      {history.map((r) => (
                        <div
                          key={r.id}
                          className={`group flex items-start justify-between gap-2 rounded-lg p-2 transition ${current?.id === r.id ? "bg-primary/10" : "hover:bg-secondary/60"}`}
                          data-testid={`agent-history-${r.id}`}
                        >
                          <button type="button" onClick={() => { setCurrent(r); setActiveFile(0); }} className="min-w-0 flex-1 text-left">
                            <div className="truncate text-xs font-medium">{r.title}</div>
                            <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
                              {r.files?.length || 0} files · {new Date(r.created_at).toLocaleDateString()}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(r.id)}
                            className="opacity-0 transition group-hover:opacity-100"
                            data-testid={`agent-history-delete-${r.id}`}
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </section>

            {/* Output / preview */}
            <section className="space-y-4 lg:col-span-7" data-testid="agent-output-col">
              {!current ? (
                <div className="flex h-[460px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/30 text-center" data-testid="agent-empty-state">
                  <Sparkles className="mb-3 h-7 w-7 text-muted-foreground" />
                  <div className="text-sm font-medium">Your generated files will appear here</div>
                  <div className="mt-1 max-w-sm text-xs text-muted-foreground">
                    Pick a template on the left or describe your own. Average run: 10-30 s.
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-emerald-600">Build complete</div>
                        <div className="mt-1 text-base font-semibold">{current.title}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{current.summary}</div>
                      </div>
                      <a
                        href={downloadAgentZip(current.id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600"
                        data-testid="agent-download-zip"
                      >
                        <Download className="h-3.5 w-3.5" /> Download ZIP
                      </a>
                    </div>
                  </div>

                  {/* File tabs */}
                  <div className="overflow-hidden rounded-2xl border border-border bg-card">
                    <div className="flex overflow-x-auto border-b border-border bg-secondary/30">
                      {hasPreview && (
                        <button
                          type="button"
                          onClick={() => setShowPreview(true)}
                          className={`flex shrink-0 items-center gap-1.5 border-r border-border px-3 py-2 text-[11px] transition ${
                            showPreview ? "bg-card font-semibold text-emerald-500" : "text-muted-foreground hover:bg-card/60"
                          }`}
                          data-testid="agent-preview-tab"
                        >
                          <Eye className="h-3 w-3" /> Live preview
                        </button>
                      )}
                      {current.files.map((f, i) => (
                        <button
                          key={f.path}
                          type="button"
                          onClick={() => { setActiveFile(i); setShowPreview(false); }}
                          className={`flex shrink-0 items-center gap-1.5 border-r border-border px-3 py-2 text-[11px] transition ${
                            !showPreview && i === activeFile ? "bg-card font-semibold text-foreground" : "text-muted-foreground hover:bg-card/60"
                          }`}
                          data-testid={`agent-file-tab-${i}`}
                        >
                          {/\.(html|css|js|ts|jsx|tsx|json|xml|yml|yaml|py|sh)$/.test(f.path) ? <FileCode2 className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                          {f.path}
                        </button>
                      ))}
                    </div>

                    {hasPreview && showPreview ? (
                      <div className="relative" data-testid="agent-preview-pane">
                        <div className="absolute right-2 top-2 z-10 flex gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => {
                              const blob = new Blob([previewSrcDoc], { type: "text/html" });
                              const url = URL.createObjectURL(blob);
                              window.open(url, "_blank", "noopener,noreferrer");
                            }}
                            className="h-7 rounded-md px-2 text-[11px]"
                            data-testid="agent-preview-open-new"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </div>
                        <iframe
                          title="Live preview"
                          srcDoc={previewSrcDoc}
                          sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
                          className="h-[520px] w-full border-0 bg-white"
                          data-testid="agent-preview-iframe"
                        />
                      </div>
                    ) : activeFileObj ? (
                      <div className="relative">
                        <div className="absolute right-2 top-2 z-10">
                          <Button variant="ghost" size="sm" onClick={() => handleCopy(activeFileObj.content)} className="h-7 rounded-md px-2 text-[11px]" data-testid="agent-copy-file">
                            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          </Button>
                        </div>
                        <ScrollArea className="h-[460px]">
                          <pre className="overflow-x-auto p-4 text-[12px] leading-relaxed font-mono" data-testid="agent-file-content">
                            <Badge variant="outline" className="mb-3 text-[10px] uppercase">{activeFileObj.language || "text"}</Badge>
                            <code className="block whitespace-pre-wrap break-words text-foreground">{activeFileObj.content}</code>
                          </pre>
                        </ScrollArea>
                      </div>
                    ) : null}
                  </div>

                  {current.next_steps?.length > 0 && (
                    <div className="rounded-2xl border border-border/60 bg-card/30 p-4">
                      <div className="mb-2 text-xs uppercase tracking-widest text-primary">Next steps</div>
                      <ul className="space-y-1.5 text-sm">
                        {current.next_steps.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-muted-foreground">
                            <span className="mt-0.5 text-primary">{i + 1}.</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
